class ZendeskService {

	private static instance: ZendeskService;
	private scriptLoaded = false;
	private loadingPromise: Promise<void> | null = null;
	// Posée sur <body> une fois le widget chargé : active le positionnement du widget (SharedDiscussions)
	private readonly LOADED_CLASS = 'zendesk-loaded';
	// Posée sur <body> tant que la fenêtre de chat est ouverte (événements de l'API Zendesk)
	private readonly OPEN_CLASS = 'zendesk-open';

	private constructor () {}

	static getInstance (): ZendeskService {
		if (!ZendeskService.instance) {
			ZendeskService.instance = new ZendeskService();
		}
		return ZendeskService.instance;
	}

	/**
	 * Charge le script Zendesk de manière asynchrone et idempotente.
	 * Le widget n'est activé que si VITE_ZENDESK_KEY est renseignée : une clé vide
	 * le désactive silencieusement, sans test d'environnement dans le code.
	 * À la déconnexion, la conversation est vidée par resetAsync (SessionManager.logoutAsync).
	 */
	async loadAsync (): Promise<void> {
		const key = import.meta.env.VITE_ZENDESK_KEY;
		if (!key?.length) return;

		// Si déjà chargé, on retourne immédiatement
		if (this.scriptLoaded) return;

		// Si un chargement est en cours, on attend qu'il se termine
		if (this.loadingPromise) return this.loadingPromise;

		// On crée une nouvelle promesse de chargement
		this.loadingPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			// /!\ Le snippet Zendesk relit sa propre balise par cet id exact : ne pas le renommer
			script.id = 'ze-snippet';
			script.src = `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(key)}`;
			script.async = true;

			script.onload = () => {
				this.scriptLoaded = true;
				document.body.classList.add(this.LOADED_CLASS);
				this.onOpen(() => document.body.classList.add(this.OPEN_CLASS));
				this.onClose(() => document.body.classList.remove(this.OPEN_CLASS));
				resolve();
			};

			// En cas d'échec (ex : CDN indisponible) : on retire le script et on réinitialise
			// l'état pour qu'un prochain appel puisse retenter.
			script.onerror = () => {
				script.remove();
				this.loadingPromise = null;
				reject(new Error('Failed to load Zendesk widget'));
			};

			document.body.appendChild(script);
		});

		return this.loadingPromise;
	}

	/** Exécute une commande Zendesk */
	private execute (command: string, ...args: any[]): void {
		if (typeof window.zE === 'function') {
			window.zE(command, ...args);
		}
	}

	/** Affiche le widget */
	show () {
		this.execute('messenger', 'show');
		return this;
	}

	/** Cache le widget */
	hide () {
		this.execute('messenger', 'hide');
		return this;
	}

	/** Ouvre le widget */
	open () {
		this.execute('messenger', 'open');
		return this;
	}

	/** Ferme le widget */
	close () {
		this.execute('messenger', 'close');
		return this;
	}

	/**
	 * Vide l'état du widget, conversation comprise (conservée dans le localStorage, un rechargement
	 * ne suffit pas), pour ne pas la laisser au prochain utilisateur du navigateur.
	 * Résout aussitôt si le widget n'est pas chargé.
	 */
	async resetAsync (): Promise<void> {
		if (typeof window.zE !== 'function') return;
		// Garde-fou : la déconnexion ne doit jamais rester bloquée sur Zendesk, ni par une attente ni par une erreur
		await Promise.race([
			new Promise<void>(resolve => this.execute('messenger', 'resetWidget', resolve)),
			new Promise<void>(resolve => setTimeout(resolve, 1000)),
		]).catch(() => {});
	}

	/** Définit la locale du widget */
	setLocale (locale: string): void {
		this.execute('messenger:set', 'locale', locale);
	}

	/** Définit le z-index du widget */
	setZIndex (zIndex: number): void {
		this.execute('messenger:set', 'zIndex', zIndex);
	}

	/** Configure les cookies du widget */
	setCookies (range: 'all' | 'functional' | 'none'): void {
		this.execute('messenger:set', 'cookies', range);
	}

	/** Définit les champs de conversation (conversation fields) */
	setConversationFields (
		fields: Array<{ id: string, value: string | number | boolean }>,
		callback?: () => void,
	): void {
		if (callback) {
			this.execute('messenger:set', 'conversationFields', fields, callback);
		}
		else {
			this.execute('messenger:set', 'conversationFields', fields);
		}
	}

	/** Définit les tags de conversation */
	setConversationTags (tags: string[]): void {
		this.execute('messenger:set', 'conversationTags', tags);
	}

	/** Personnalise le thème du widget */
	setCustomization (theme: {
		primary?: string
		onPrimary?: string
		message?: string
		onMessage?: string
		action?: string
		onAction?: string
		businessMessage?: string
		onBusinessMessage?: string
		[key: string]: string | undefined
	}): void {
		this.execute('messenger:set', 'customization', { theme });
	}

	/** Écoute li18n.global.t('common.eventOf')ouverture du widget */
	onOpen (callback: () => void): void {
		this.execute('messenger:on', 'open', callback);
	}

	/** Écoute l'événement de fermeture du widget */
	onClose (callback: () => void): void {
		this.execute('messenger:on', 'close', callback);
	}

	/** Écoute les changements du nombre de messages non lus */
	onUnreadMessages (callback: (count: number) => void): void {
		this.execute('messenger:on', 'unreadMessages', callback);
	}

}

export default function useZendeskService () {
	return ZendeskService.getInstance();
}
